import {useMemo} from 'react';
import type {FieldLayout} from 'schema/types';
import type {Part} from '../../../schemaDefinition/types';
import {useFormSchemaGlobalScopeContext} from '../../contexts';
import {useDataFormConfigurationContext} from '../contexts/DataFormConfigurationContext';

const layoutConfig: {[key: string]: FieldLayout} = {
  // Pr schema layouts
  'publisher.name': {maxWidth: '14.4rem'},

  // Global field layouts
  place: {flex: 1},
  bibleVersion: {flex: 1},
  bibleContent: {flex: 1},
  country: {flex: 1},
  countryOfBirth: {flex: 1},
  countryFlourishedIn: {flex: 1},
  deweyNumber: {width: '14.4rem'},
  deweySource: {width: '7.2rem'},
  edition: {maxWidth: '4.8rem'},
  ean: {width: '10rem'},
  firstPublishedYear: {maxWidth: '7.2rem'},
  occupationalField: {flex: 1},
  isbn: {width: '10rem'},
  ismn: {width: '10rem'},
  isni: {width: '10rem'},
  productOwner: {flex: 1},
  nationalId: {maxWidth: '13.2rem'},
  profession: {flex: 1},
  content: {flex: 1},
  productForm: {flex: 1},
  productFormDetail: {flex: 1},
  productGroup: {flex: 1},
  bookGroup: {flex: 1},
  publishedYear: {maxWidth: '7.2rem'},
  type: {width: '7.2rem'},

  intermediateOriginalLanguage: {flex: 1},

  dimensions: {flex: 2, maxWidth: '12rem'},
  weight: {flex: 1, maxWidth: '7.2rem'},
  pageCount: {maxWidth: '7.2rem'},
  unnumberedPages: {maxWidth: '7.2rem'},
  runtime: {flex: 2, maxWidth: '12rem'},
  scale: {flex: 1, maxWidth: '15.6rem'},
  colour: {flex: 2},

  // Pr part type layout
  'type:date': {maxWidth: '12rem'},
  'type:year': {width: '7.2rem'},
  'type:yearOrDate': {maxWidth: '12rem'},
};

export const getLayout = (key: string): FieldLayout | undefined =>
  layoutConfig[key];

export const useLayout = (part: Part): FieldLayout | undefined => {
  const {schema} = useFormSchemaGlobalScopeContext();
  const config = useDataFormConfigurationContext();
  return useMemo(() => {
    const partLayout =
      (part.name ? config.parts?.[part.name]?.layout : undefined) ??
      (part.key ? config.parts?.[part.key]?.layout : undefined);
    if (partLayout) {
      return partLayout;
    }

    const scopedKey =
      schema.key && part.name ? `${schema.key}.${part.name}` : undefined;
    return (
      (scopedKey ? layoutConfig[scopedKey] : undefined) ??
      (part.name ? layoutConfig[part.name] : undefined) ??
      layoutConfig[`type:${part.type}`] ??
      (part.key ? layoutConfig[part.key] : undefined) ??
      undefined
    );
  }, [config.parts, part, schema.key]);
};
